import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Stack,
  Select,
  Badge,
  useToast,
  Input,
  InputGroup,
  InputLeftElement,
  Heading,
  Grid,
  GridItem,
  Icon,
  IconButton,
  Skeleton,
} from "@chakra-ui/react";
import { FiUsers, FiSearch } from "react-icons/fi";
import { ImFilter } from "react-icons/im";
import { GoChevronLeft, GoChevronRight } from "react-icons/go";
import { FaRupeeSign, FaMoneyBillWave, FaChartLine } from "react-icons/fa";
import { HiUserGroup } from "react-icons/hi";
import { Loading } from "../../../components/Loading";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { getCustomersFinance, getFinanceStatistics } from "../../../actions/finance/customerAccount.action";
import { useNavigate } from "react-router-dom";

const CustomerAccount = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const debounceTimer = useRef(null);

  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [stats, setStats] = useState({
    totalCustomers: 0,
    individualCustomers: 0,
    corporateCustomers: 0,
    totalRevenue: 0,
    totalPending: 0,
  });

  // Fetch stats
  const loadStats = async () => {
    try {
      const res = await getFinanceStatistics();
      if (res.success) {
        setStats(res.data);
      }
    } catch (e) {
      console.error("Error fetching stats:", e);
    }
  };

  // Fetch customer data
  const loadCustomerData = async () => {
    setFetching(true);
    try {
      const res = await getCustomersFinance(
        currentPage, 
        itemsPerPage,
        searchTerm,
        typeFilter
      );
      
      if (res.success) {
        setData(res.data);
        setTotalPages(res.pagination.totalPages);
        setTotalItems(res.pagination.total);
      }
    } catch (e) {
      console.error("Error fetching customers:", e);
      toast({
        title: "Failed to fetch customers",
        description: e.message || "An error occurred",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setFetching(false);
      setLoading(false);
    }
  };

  // Debounce search
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(debounceTimer.current);
  }, [searchInput]);

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    loadCustomerData();
  }, [currentPage, itemsPerPage, searchTerm, typeFilter]);

  const clearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    setTypeFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleRowClick = (customer) => {
    navigate(`/finance/customer/${customer._id}`);
  };

  if (loading) return <Loading />;

  return (
    <Stack w={"100%"} justifyContent={"center"} spacing={6} p={6}>
      {/* Breadcrumbs */}
      <BreadCrumbMaker
        items={[
          { title: "Finance", link: "#" },
          { title: "Customer List", link: "/finance/customer", on: true },
        ]}
      />

      {/* Stats Cards */}
      <Box>
        <Grid
          gridGap={6}
          templateColumns={{
            base: "repeat(2, 1fr)",
            md: "repeat(2, minmax(250px, 1fr))",
            lg: "repeat(4, minmax(250px, 1fr))",
          }}
        >
          <GridItem>
            <Box
              bg="white"
              p={4}
              rounded="md"
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              h="120px"
              gap={2}
              borderRadius="3xl"
            >
              <Box
                bg={"#E7EDFF"}
                w={18}
                h={18}
                p={30}
                mx={4}
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="full"
              >
                <Icon as={FiUsers} boxSize={6} color={"#396AFF"} />
              </Box>
              <Flex flexDirection={"column"} alignItems={"flex-start"}>
                <Text fontSize="16px" color="#718EBF">
                  Total Customers
                </Text>
                <Text fontSize="25px">{stats.totalCustomers || 0}</Text>
              </Flex>
            </Box>
          </GridItem>

          <GridItem>
            <Box
              bg="white"
              p={4}
              rounded="md"
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              h="120px"
              gap={2}
              borderRadius="3xl"
            >
              <Box
                bg={"#FFF5D9"}
                w={18}
                h={18}
                p={30}
                mx={4}
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="full"
              >
                <Icon as={HiUserGroup} boxSize={6} color={"#FFBB38"} />
              </Box>
              <Flex flexDirection={"column"} alignItems={"flex-start"}>
                <Text fontSize="16px" color="#718EBF">
                  Individual
                </Text>
                <Text fontSize="25px">{stats.individualCustomers || 0}</Text>
              </Flex>
            </Box>
          </GridItem>

          <GridItem>
            <Box
              bg="white"
              p={4}
              rounded="md"
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              h="120px"
              gap={2}
              borderRadius="3xl"
            >
              <Box
                bg={"#FFE0EB"}
                w={18}
                h={18}
                p={30}
                mx={4}
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="full"
              >
                <Icon as={HiUserGroup} boxSize={6} color={"#FF82AC"} />
              </Box>
              <Flex flexDirection={"column"} alignItems={"flex-start"}>
                <Text fontSize="16px" color="#718EBF">
                  Corporate
                </Text>
                <Text fontSize="25px">{stats.corporateCustomers || 0}</Text>
              </Flex>
            </Box>
          </GridItem>

          <GridItem>
            <Box
              bg="white"
              p={4}
              rounded="md"
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              h="120px"
              gap={2}
              borderRadius="3xl"
            >
              <Box
                bg={"#DCFCE7"}
                w={18}
                h={18}
                p={30}
                mx={4}
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="full"
              >
                <Icon as={FaRupeeSign} boxSize={6} color={"#16A34A"} />
              </Box>
              <Flex flexDirection={"column"} alignItems={"flex-start"}>
                <Text fontSize="16px" color="#718EBF">
                  Total Revenue (₹)
                </Text>
                <Text fontSize="25px">₹{(stats.totalRevenue || 0).toLocaleString()}</Text>
              </Flex>
            </Box>
          </GridItem>
        </Grid>
      </Box>

      {/* Header with Search and Filters */}
      <Flex
        justifyContent={"space-between"}
        flexDirection={{ base: "column", md: "row" }}
        gap={4}
      >
        <Heading fontSize={"xl"} py={1} color={"#333B69"}>
          Customer List
        </Heading>
        
        <Flex w="100%" maxW="600px" gap={3} justifyContent={"flex-end"}>
          <Flex
            gap={0}
            alignItems="center"
            w="100%"
            maxW="300px"
            bg="white"
            px={3}
            h="40px"
            borderRadius={"13"}
          >
            <FiSearch color="#718EBF" fontSize="1rem" />
            <Input
              placeholder="Search by name, mobile..."
              border="none"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              _focus={{ outline: "none", boxShadow: "none" }}
              fontSize="sm"
              h="full"
              _placeholder={{ color: "#718EBF" }}
            />
            {searchInput && (
              <IconButton
                aria-label="Clear search"
                icon={<Text fontSize="lg">×</Text>}
                size="sm"
                variant="ghost"
                onClick={clearSearch}
              />
            )}
          </Flex>

          <Flex
            gap={2}
            alignItems="center"
            justifyContent="center"
            w="100%"
            maxW="162px"
            bg="white"
            px={3}
            h="40px"
            borderRadius={"13"}
          >
            <ImFilter color="#718EBF" fontSize="1rem" />
            <Select
              fontSize="sm"
              width="auto"
              value={typeFilter}
              onChange={handleFilterChange}
              placeholder="All Types"
              h="28px"
              border="none"
              _focus={{ outline: "none", boxShadow: "none" }}
            >
              <option value="Individual">Individual</option>
              <option value="Corporate">Corporate</option>
            </Select>
          </Flex>
        </Flex>
      </Flex>

      {/* Table */}
      <Box bg="white" p={6} borderRadius="3xl" overflowX="auto">
        <Table size="sm" bg="white" fontSize="small">
          <Thead>
            <Tr>
              <Th color="#718EBF">SR. NO</Th>
              <Th color="#718EBF">NAME</Th>
              <Th color="#718EBF">MOBILE NO.</Th>
              <Th color="#718EBF">TYPE</Th>
              <Th color="#718EBF">RATING</Th>
              <Th color="#718EBF">TOTAL BOOKINGS</Th>
              <Th color="#718EBF">TOTAL PAYMENT</Th>
              <Th color="#718EBF">REMAINING AMOUNT</Th>
            </Tr>
          </Thead>

          <Tbody>
            {fetching ? (
              <Tr>
                <Td colSpan={8} p={4}>
                  <Stack spacing={3}>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Skeleton key={i} height="60px" borderRadius="md" />
                    ))}
                  </Stack>
                </Td>
              </Tr>
            ) : data.length ? (
              data.map((row, index) => (
                <Tr 
                  key={index} 
                  _hover={{ bg: "gray.50", cursor: "pointer" }}
                  onClick={() => handleRowClick(row)}
                >
                  <Td>{row.srNo}</Td>
                  <Td>{row.name}</Td>
                  <Td>{row.mobile}</Td>
                  <Td>
                    <Badge
                      colorScheme={row.type === "Individual" ? "blue" : "purple"}
                      px={2}
                      py={1}
                      borderRadius="md"
                    >
                      {row.type}
                    </Badge>
                  </Td>
                  <Td>
                    <Flex align="center" gap={1}>
                      ⭐ {row.rating}
                    </Flex>
                  </Td>
                  <Td>{row.totalBookings}</Td>
                  <Td>₹{row.totalPayment?.toLocaleString()}</Td>
                  <Td>
                    <Text color={row.remainingAmount > 0 ? "red.500" : "green.500"}>
                      ₹{row.remainingAmount?.toLocaleString()}
                    </Text>
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={8} textAlign="center" py={8}>
                  <Text color="#718EBF">No data found</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>

        {/* Pagination */}
        <Flex justify="space-between" align="center" mt={4}>
          <Flex gap={4} align="center">
            <Select
              w="fit-content"
              size="sm"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              {[10, 20, 50].map((n) => (
                <option key={n}>{n}</option>
              ))}
            </Select>
            <Text fontSize="sm" color="gray.600">
              Showing {data.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to{" "}
              {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} customers
            </Text>
          </Flex>

          <Flex gap={2} align="center">
            <Button
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              isDisabled={currentPage === 1}
            >
              <GoChevronLeft />
            </Button>
            <Text fontSize="sm" px={3}>
              Page {currentPage} of {totalPages}
            </Text>
            <Button 
              size="sm" 
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              isDisabled={currentPage >= totalPages}
            >
              <GoChevronRight />
            </Button>
          </Flex>
        </Flex>
      </Box>
    </Stack>
  );
};

export default CustomerAccount;

